import React, { useContext, useEffect, useRef } from "react";

import { Box, Stack, Text } from "@mantine/core";

import { useResize } from "hooks/size";
import { useIsSmallerThan } from "ui/theme/helpers";

import { Eyebrow } from "./mantine-extended/eyebrow";
import {
  FIXED_SIZE_RANGE_BAR_HIGH_BOUNDARY,
  FIXED_SIZE_RANGE_BAR_LOW_BOUNDARY,
  RangeBarWithFixedSize,
  RANGE_BAR_TRANSITION_DURATION,
} from "./RangeBar";

const RangeBarContext = React.createContext({
  percentWidthRangeStart: 0,
  percentWidthRangeEnd: 0,
  sizeVariant: "unknown" as "small" | "large" | "xs" | "unknown",
});

interface DetailedRangeBarProps {
  rangeMin: number;
  rangeLow: number;
  rangeHigh: number;
  rangeMax: number;
}

export function BigRangeBar({ rangeMin, rangeLow, rangeHigh, rangeMax }: DetailedRangeBarProps) {
  const totalRange = rangeMax - rangeMin;

  const percentRangeStart = (100 * (rangeLow - rangeMin)) / totalRange;
  const percentRangeEnd = (100 * (rangeHigh - rangeMin)) / totalRange;

  const rangeMid = (rangeHigh + rangeLow) / 2;

  const rangeWingValue = Math.round((100 * (rangeHigh - rangeMid)) / rangeMid);

  const rangeWidthRef = useRef<HTMLDivElement | null>(null);
  const { width: rangeWidth, remeasure } = useResize(rangeWidthRef);

  useEffect(() => {
    remeasure();
  }, [rangeMin, rangeLow, rangeHigh, rangeMax, remeasure]);

  const small = useIsSmallerThan("md");
  const xs = useIsSmallerThan("sm");
  let sizeVariant: "small" | "large" | "xs" | "unknown" = xs ? "xs" : small ? "small" : "large";

  return (
    <RangeBarContext.Provider
      value={{
        percentWidthRangeStart: percentRangeStart,
        percentWidthRangeEnd: percentRangeEnd,
        sizeVariant,
      }}
    >
      <Box pos="relative" my="50px">
        <Box
          w="100%"
          h={3}
          sx={{
            display: "flex",
            backgroundColor: "var(--mantine-color-brand-6)",
            boxShadow: `0 0 3px 4px var(--mantine-color-brand-2)`,
            clipPath: "inset(-10px 3px -10px 3px)",
          }}
        />

        {/* a dummy element that matches the range's width so we can perform logic */}
        <Box
          ref={rangeWidthRef}
          sx={{
            position: "absolute",
            left: `${percentRangeStart}%`,
            width: `${percentRangeEnd - percentRangeStart}%`,
          }}
        />

        <Box
          w="100%"
          sx={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
            alignItems: "center",
            position: "absolute",
            top: 0,
            bottom: 0,
          }}
        >
          <Flag label="Low">{`$${Math.round(rangeLow).toLocaleString("en-US")}`}</Flag>
          {sizeVariant !== "xs" && <Wing>{`${rangeWingValue.toLocaleString("en-US")}%`}</Wing>}
          <Flag label="Mid" exaggerated>{`$${Math.round(rangeMid).toLocaleString("en-US")}`}</Flag>
          {sizeVariant !== "xs" && <Wing>{`${rangeWingValue.toLocaleString("en-US")}%`}</Wing>}
          <Flag label="High">{`$${Math.round(rangeHigh).toLocaleString("en-US")}`}</Flag>
        </Box>
      </Box>
    </RangeBarContext.Provider>
  );
}

export function SinglePointRangeBarWithFlag({ value }: { value: number }) {
  const percent =
    ((value - FIXED_SIZE_RANGE_BAR_LOW_BOUNDARY) /
      (FIXED_SIZE_RANGE_BAR_HIGH_BOUNDARY - FIXED_SIZE_RANGE_BAR_LOW_BOUNDARY)) *
    100;

  return (
    <Box pos="relative" mt="75px">
      <RangeBarWithFixedSize lowValue={value} highValue={value} />
      <Box
        sx={{
          position: "absolute",
          left: `calc(${percent}% + 6px)`,
        }}
      >
        <Flag exaggerated startAtTop>{`$${value.toLocaleString("en-US")}`}</Flag>
      </Box>
    </Box>
  );
}

interface FlagProps {
  children: string;
  exaggerated?: boolean;
  startAtTop?: boolean;
  label?: string;
}

function Flag(props: FlagProps) {
  const { sizeVariant } = useContext(RangeBarContext);

  return (
    <Box className="wing-parent">
      <Stack
        gap={4}
        className="flag-parent"
        align="center"
        sx={{
          justifyContent: "center",
          backgroundColor: "var(--mantine-color-gray-0)",
          border: "1px solid #B4B8B7",
          borderRadius: "5px",
          padding: "12px 24px",
          boxShadow: "0px 1px 3px rgba(0, 0, 0, 0.04), 0px 1px 2px rgba(0, 0, 0, 0.08)",

          ...(props.exaggerated && {
            color: "var(--mantine-color-brand-8)",
            backgroundColor: "var(--mantine-color-brand-0)",
            borderColor: "var(--mantine-color-brand-6)",
            fontSize: "60px",
          }),

          ...(sizeVariant === "small" && {
            padding: "8px 16px",
          }),

          ...(sizeVariant === "xs" && {
            padding: "4px 8px",
          }),
        }}
      >
        {props.label && (
          <Text
            fz={props.exaggerated ? 16 : 14}
            fw={props.exaggerated ? 500 : 400}
            sx={{
              color: props.exaggerated
                ? "var(--mantine-color-brand-8)"
                : "var(--mantine-color-gray-5)",

              ...(sizeVariant === "small" && {
                fontSize: props.exaggerated ? "12px" : "10px",
              }),

              ...(sizeVariant === "xs" && {
                fontSize: props.exaggerated ? "10px" : "8px",
              }),
            }}
          >
            {props.label}
          </Text>
        )}
        <Eyebrow
          className="flag"
          // isaac- this is silly to get the flag to be the right size
          size={props.exaggerated ? ("lg" as "md") : "md"}
          sx={{
            lineHeight: "1",
            fontWeight: 500,
            color: "var(--mantine-color-gray-8)",
            fontSize: "24px",
            transition: `transform ${RANGE_BAR_TRANSITION_DURATION} ease-in-out`,

            ...(props.exaggerated && {
              color: "var(--mantine-color-brand-6)",
              fontSize: "60px",
            }),

            ...(sizeVariant === "small" && {
              fontSize: props.exaggerated ? "40px" : "18px",
            }),

            ...(sizeVariant === "xs" && {
              fontSize: props.exaggerated ? "30px" : "14px",
            }),
          }}
        >
          {props.children}
        </Eyebrow>
      </Stack>
    </Box>
  );
}

function Wing({ children }: { children: string }) {
  return (
    <Box
      sx={{
        width: "100%",
        position: "relative",
        display: "flex",
        alignItems: "center",
      }}
    >
      <Box
        w="100%"
        bg="gray.4"
        sx={{
          position: "absolute",
          top: "50%",
        }}
      />
      <Eyebrow
        size="sm"
        sx={{
          border: "1px solid #B4B8B7",
          color: "var(--mantine-color-gray-6)",
          backgroundColor: "var(--mantine-color-gray-1)",
          borderRadius: "3px",
          textAlign: "center",
          zIndex: 10,
          margin: "0 auto",
          fontSize: "14px",
          padding: "1px 2px",
        }}
      >
        {children}
      </Eyebrow>
    </Box>
  );
}
